import * as v from "valibot";
import { type InvokeOptions, isUnauthorized, prepareRequest, renewAuthorization, sendRequest } from "./http-transport";
import type { HttpMethod, Schema } from "./types";
import { sendRequestWithProgress, supportsUploadProgress, type UploadProgress } from "./upload-transport";

export const emptySchema = v.object({});
export type EmptyRequest = v.InferOutput<typeof emptySchema>;

export type ResponseSchema<TRes> = v.BaseSchema<unknown, TRes, v.BaseIssue<unknown>>;

export class ApiContract<TReq extends Record<string, unknown>, TRes> {
  readonly _response!: TRes; // phantom type for type inference

  constructor(
    public readonly method: HttpMethod,
    public readonly path: string,
    public readonly schema: Schema<TReq>,
    public readonly responseSchema?: ResponseSchema<TRes>,
  ) {}

  async invoke(request?: TReq, options?: InvokeOptions): Promise<TRes> {
    const prepared = await prepareRequest(this, request, options);
    try {
      return await sendRequest<TRes>(this.method, prepared);
    } catch (error) {
      if (!isUnauthorized(error) || !(await renewAuthorization(options))) {
        throw error;
      }
      return sendRequest<TRes>(this.method, await prepareRequest(this, request, options));
    }
  }

  async upload(request: TReq, onProgress: (progress: UploadProgress) => void, options?: InvokeOptions): Promise<TRes> {
    if (!supportsUploadProgress()) {
      return this.invoke(request, options);
    }
    const prepared = await prepareRequest(this, request, options);
    return sendRequestWithProgress<TRes>(this.method, prepared, onProgress);
  }
}

export class ContractWithRequest<TReq extends Record<string, unknown>> extends ApiContract<TReq, void> {
  withResponse<TEntries extends v.ObjectEntries>(entries: TEntries): ApiContract<TReq, v.InferOutput<v.ObjectSchema<TEntries, undefined>>> {
    const responseSchema = v.object(entries);
    type TRes = v.InferOutput<typeof responseSchema>;
    return new ApiContract<TReq, TRes>(this.method, this.path, this.schema, responseSchema);
  }
}

export class ContractBuilder extends ApiContract<EmptyRequest, void> {
  constructor(method: HttpMethod, path: string) {
    super(method, path, emptySchema, undefined);
  }

  withRequest<TReq extends v.ObjectEntries>(entries: TReq): ContractWithRequest<v.InferOutput<v.ObjectSchema<TReq, undefined>>> {
    const schema = v.object(entries);
    return new ContractWithRequest<v.InferOutput<v.ObjectSchema<TReq, undefined>>>(this.method, this.path, schema, undefined);
  }

  withResponse<TRes extends v.ObjectEntries>(entries: TRes): ApiContract<EmptyRequest, v.InferOutput<v.ObjectSchema<TRes, undefined>>> {
    const responseSchema = v.object(entries);
    type TResponse = v.InferOutput<typeof responseSchema>;
    return new ApiContract<EmptyRequest, TResponse>(this.method, this.path, emptySchema, responseSchema);
  }
}

export const api = (method: HttpMethod, path: string): ContractBuilder => new ContractBuilder(method, `/api${path}`);
